import User from "../../model/user";
import { $axios } from "../../config/axios";
import { cloneDeep } from "lodash";
import userService from "@/services/userService";
import moment from "moment";
import localStore from "@/utils/localStore";
import {
  groupPermissions,
  FTPermissions,
  FTSchEditPermissions,
} from "@/middleware/pageAccessManager";

const storeUserTypes = {
  admin: "ADMIN",
  pond_owner: "POND_OWNER",
  pond_worker: "POND_WORKER",
  pond_technician: "POND_TECHNICIAN",
  pond_manager: "POND_MANAGER",
  no_access: "NO_ACCESS",
  super_admin: "SUPER_ADMIN",
};

const state = {
  user: {},
  UAMList: [],
  UAMListNoAccess: [],
  currUserLocation: undefined,
  userLocations: [],
  errorAlerts: [],
  timezones: [],
  selectTimeZone: {},
  filters: {
    page: 1,
    limit: 30,
  },
};
const getters = {
  getUserProfile: (state) => {
    return state.user;
  },
  getUserId: (state, getters) => {
    return getters.getUserProfile._id;
  },
  isReadOnly: (state) => {
    if (!state.user) {
      return false;
    }
    return state.user.is_read_only;
  },
  getPermissions: (state, getters) => {
    return groupPermissions(getters.getUserProfile.permissions);
  },
  getBkPermissions: (state, getters) => {
    return getters.getUserProfile.permissions;
  },
  canUserAccessApiByBkPermissions: (state, getters) => {
    const userType = getters.getCurrUserType;
    const isPondOwnerOrManager = [
      storeUserTypes.admin,
      storeUserTypes.pond_owner,
      storeUserTypes.pond_manager,
    ].includes(userType);
    const bkPermissions = getters.getBkPermissions;
    if (isPondOwnerOrManager) {
      return () => true;
    } else {
      return (requirePermissions) => {
        return requirePermissions.some((permission) =>
          bkPermissions.includes(permission)
        );
      };
    }
  },
  doRestrictHighPriorityUsers: (state, getters) => {
    const { can_edit_schedules, can_edit_settings } = getters.getUserProfile;
    return can_edit_schedules || can_edit_settings;
  },
  getViewPermissions: (state, getters) => {
    const userType = getters.getCurrUserType;
    const isPondOwnerOrManager = [
      storeUserTypes.admin,
      storeUserTypes.pond_owner,
      storeUserTypes.pond_manager,
    ].includes(userType);
    return FTPermissions.reduce((acc, curr) => {
      acc[curr] = isPondOwnerOrManager || getters.getPermissions[curr].VIEW;
      return acc;
    }, {});
  },
  getUpdatePermissions: (state, getters) => {
    const userType = getters.getCurrUserType;
    const isPondOwnerOrManager = [
      storeUserTypes.admin,
      storeUserTypes.pond_owner,
      storeUserTypes.pond_manager,
    ].includes(userType);
    // only true settings and schedules

    const { can_edit_schedules, can_edit_settings } = getters.getUserProfile;

    if (can_edit_schedules || can_edit_settings) {
      return FTSchEditPermissions.reduce((acc, curr) => {
        acc[curr] = curr === "MANAGE_EDIT_SCHEDULES";
        return acc;
      }, {});
    } else {
      return FTPermissions.reduce((acc, curr) => {
        acc[curr] = isPondOwnerOrManager || getters.getPermissions[curr].UPDATE;
        return acc;
      }, {});
    }
  },
  getDeletePermissions: (state, getters) => {
    const userType = getters.getCurrUserType;
    const isPondOwnerOrManager = [
      storeUserTypes.admin,
      storeUserTypes.pond_owner,
      storeUserTypes.pond_manager,
    ].includes(userType);
    const { can_edit_schedules, can_edit_settings } = getters.getUserProfile;

    if (can_edit_schedules || can_edit_settings) {
      return FTSchEditPermissions.reduce((acc, curr) => {
        acc[curr] = curr === "MANAGE_EDIT_SCHEDULES";
        return acc;
      }, {});
    } else {
      return FTPermissions.reduce((acc, curr) => {
        acc[curr] = isPondOwnerOrManager || getters.getPermissions[curr].DELETE;
        return acc;
      }, {});
    }
  },
  getMomentObjectUserTZ(state, getters) {
    return () =>
      cloneDeep(
        moment()
          .utc()
          .utcOffset(getters.getUserTimeZoneOffset)
      );
  },
  getProfileUrl: (state) => {
    return state.user.company_logo
      ? `${state.user.company_logo}?${state.user.updated_at}`
      : undefined;
  },
  getCurrUserType: (state) => {
    return state.user.user_type;
  },
  getUserCountryName: (state) => {
    return state.user.country.name;
  },
  getUserCountryCode: (state) => {
    return state.user.country.code;
  },
  getUserTimeZone: (state, getters) => {
    return getters.getUserProfile.timezone;
  },
  getUserTimeZoneOffset: (state, getters) => {
    return getters.getUserProfile.timezone.offset;
  },
  getUserLocationsObj: (state) => {
    const locations = {};
    cloneDeep(state.userLocations)
      .sort((a, b) => {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      })
      .forEach((location) => {
        if (location.status === "ACTIVE" || location.status === undefined) {
          locations[location._id] = location;
        }
      });
    return locations;
  },
  getUserLocations: (state) => {
    return state.userLocations.filter((x) => x.status === "ACTIVE");
  },
  getCurrUserLocation: (state, getters) => {
    if (state.currUserLocation) {
      return state.currUserLocation;
    } else if (getters.getUserLocations.length > 0) {
      return getters.getUserLocations[0];
    } else {
      return {};
    }
  },
  getTimeZones(state) {
    const timezonesObj = {};
    state.timezones.forEach((timezone) => {
      timezonesObj[timezone.name] = timezone;
    });
    return timezonesObj;
  },
  getFiltersCount: (state) => {
    const filtersCount = Object.values(state.filters);
    return filtersCount.length;
  },
  getUAMListArray: (state) => {
    const fitleredUsers = state.UAMList.filter(
      (user) => user.user_type !== "NO_ACCESS"
    );

    return [...fitleredUsers, ...state.UAMListNoAccess];
  },
  getUAMListNoAccessArray: (state) => {
    return state.UAMListNoAccess;
  },

  getUAMList: (state) => {
    const userTypes = {
      pond_owner: [],
      pond_technician: [],
      pond_manager: [],
      pond_worker: [],
      no_access: [],
      admin: [],
    };
    state.UAMList.forEach((user) => {
      let userObj = new User();
      userObj = cloneDeep(user);
      userTypes[
        userObj.user_type === storeUserTypes.admin
          ? storeUserTypes.pond_owner.toLowerCase()
          : userObj.user_type.toLowerCase()
      ].push(userObj);
    });
    return userTypes;
  },
};
const mutations = {
  SET_USER_DATA(state, userData) {
    if (userData != null) {
      var user = new User();
      user.castToUserObj(userData);
      const userId = userData._id;
      const userType = userData.user_type;
      if (
        user.user_type === storeUserTypes.admin ||
        user.user_type === storeUserTypes.pond_owner
      ) {
        user.is_admin = true;
      } else if (user.user_type === storeUserTypes.super_admin) {
        user.permissions = [storeUserTypes.super_admin];
      }
      localStore.setItem(`${userType}-${userId}-trites`, user);
      state.user = user;
    } else {
      state.user = null;
    }
  },
  SET_CURR_DEVICE_LOCATION(state, location) {
    state.currUserLocation = location;
  },
  SET_USER_LOCATIONS(state, locations) {
    state.userLocations = locations;
  },
  SET_ERROR_ALERTS(state, errorAlerts) {
    state.errorAlerts = errorAlerts;
  },
  SET_TIMEZONES_LIST(state, timezones) {
    state.timezones = timezones;
  },
  SET_UAM_LIST(state, UAMList) {
    state.UAMList = UAMList;
  },
  SET_UAM_LIST_NO_ACCESS(state, UAMList) {
    console.log(UAMList, "SET");
    state.UAMListNoAccess = UAMList;
  },
};
const actions = {
  /**
   * Actions for user
   */
  fetchProfilePicS3SaveUrl: async function() {
    const response = await userService.getProfilePictureAWSS3URL();
    return response.data.data;
  },
  saveProfilePictureToTargetUrl: async function(context, file) {
    const { target_url: targetUrl, file_id } = await context.dispatch(
      "fetchProfilePicS3SaveUrl"
    );
    await userService.saveProfilePictureToTargetUrl(targetUrl, file);
    await userService.informUploadStatusToBackend(file_id);
  },
  fetchUserProfile: (context) => {
    return new Promise((resolve, reject) => {
      $axios
        .get("/profile")
        .then(async (response) => {
          context.commit("SET_USER_DATA", response.data.user_details);
          const userDetails = response.data.user_details;
          resolve(userDetails);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  updatePassword: (context, updatePasswordDetails) => {
    return new Promise((resolve, reject) => {
      $axios
        .post("/password/update", updatePasswordDetails)
        .then((response) => {
          resolve(response.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  updateUserProfile: (context, user) => {
    return new Promise((resolve, reject) => {
      $axios
        .post("/profile", user)
        .then((response1) => {
          context
            .dispatch("fetchUserProfile")
            .then((response2) => {
              resolve(response1.data);
            })
            .catch((err) => {
              reject(err);
            });
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  fetchTimeZones: (context, country_code) => {
    return new Promise((resolve, reject) => {
      $axios
        .get(`/countries/${country_code}/timezones`)
        .then(async (response) => {
          context.commit("SET_TIMEZONES_LIST", response.data.timezones);
          resolve("success");
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  /**
   * Actions for locations
   */
  fetchUserLocations: async (context) => {
    const response = await $axios.get("/users/locations");
    const userLocations = response.data.user_locations;
    context.commit("SET_USER_LOCATIONS", userLocations);
    const current_location = context.getters.getCurrUserLocation;
    const locations = context.getters.getUserLocationsObj;
    const noOflocations = Object.keys(locations).length;
    const hasLocations = noOflocations > 0;
    if (!hasLocations) return undefined;
    const isCurrLocationInvalid = locations[current_location._id] === undefined;
    if (isCurrLocationInvalid) {
      return userLocations.find((x) => x.status === "ACTIVE");
    }
    const isCurrLocationNameChanged =
      locations[current_location._id].name !==
      current_location.name.toLowerCase();
    if (isCurrLocationNameChanged) {
      return locations[current_location._id];
    }
    return current_location;
  },
  createLocation: function(context, location) {
    return new Promise((resolve, reject) => {
      $axios
        .post("/users/locations", location)
        .then((response1) => {
          resolve(response1.data.location);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  updateCurrUserLocation: (context, location) => {
    context.commit("SET_CURR_DEVICE_LOCATION", location);
  },
  updateLocationName: async (context, { locationId, location_name }) => {
    await $axios.patch(`/users/locations/${locationId}`, { location_name });
    const currLocation = await context.dispatch("fetchUserLocations");
    return currLocation;
  },
  deleteLocation: async (context, locationId) => {
    const response = await $axios.delete(`/users/locations/${locationId}`);
    const location = await context.dispatch("fetchUserLocations");
    await context.dispatch("updateCurrUserLocation", location);
    return response;
  },

  /**
   *  Actions for Error Alerts
   */
  fetchPMErrorHistory: (context, { pondmothercode, params }) => {
    return new Promise((resolve, reject) => {
      $axios
        .get(`/ponds/pond-mothers/${pondmothercode}/sensor-data-alerts/`, {
          params,
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  /**
   *Subuser Actions
   */
  createSubUser(context, userData) {
    return new Promise((resolve, reject) => {
      $axios
        .post(`/users/sub-users`, userData)
        .then((response) => {
          resolve(response.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  updateSubUser(context, { userId, payload }) {
    return new Promise((resolve, reject) => {
      $axios
        .patch(`/users/sub-users/${userId}`, payload)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updatePasswordSubUser(context, { userId, payload }) {
    return new Promise((resolve, reject) => {
      $axios
        .post(`/users/sub-users/${userId}/update-password`, payload)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  fetchNoAccessSubUserDetails(context, params) {
    return new Promise((resolve, reject) => {
      $axios
        .get(`/users/sub-users`, { params })
        .then((response) => {
          console.log(response);
          const userList = response.data.sub_users;
          console.log(userList, "resp");
          context.commit("SET_UAM_LIST_NO_ACCESS", userList);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  fetchSubUserDetails(context, params) {
    if (!params.location_id) {
      context.commit("SET_UAM_LIST", []);
    }

    return new Promise((resolve, reject) => {
      $axios
        .get(`/users/sub-users`, { params })
        .then((response) => {
          console.log(response);
          const userList = response.data.sub_users;
          userList.splice(0, 0, context.getters.getUserProfile);
          context.commit("SET_UAM_LIST", userList);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};

export default {
  namespaced: "true",
  state,
  getters,
  mutations,
  actions,
};
